export type LjudLjusCitySlug = 'malmo' | 'lund' | 'helsingborg';

export const LJUD_LJUS_CITY_SLUGS: LjudLjusCitySlug[] = ['malmo', 'lund', 'helsingborg'];

/** Optional Sound Expansion prose (Malmö only) — body H2, not page H1. */
export type LjudLjusSoundPaRentalSection = {
  id: string;
  title: string;
  paragraphs: string[];
  /** Mid-sentence disambig link in the last paragraph. */
  hogtalareLinkAnchor: string;
  hogtalareLinkPath: '/hyra-hogtalare-malmo';
};

export type LjudLjusCityConfig = {
  slug: LjudLjusCitySlug;
  path: string;
  displayName: string;
  breadcrumbLabel: string;
  serviceSchemaName: string;
  serviceSchemaDescription: string;
  heroEyebrow: string;
  heroTitle: string;
  heroLead: string;
  seoSectionId: string;
  seoSectionTitle: string;
  introParagraphs: string[];
  quotePrimary: string;
  quoteSeo: string;
  ctaLabel: string;
  geo: { latitude: number; longitude: number };
  showMalmoDeepLinks: boolean;
  /** Sound Expansion FINAL GO — only set for malmo. */
  soundPaRentalSection?: LjudLjusSoundPaRentalSection;
  faqItems: { q: string; a: string }[];
};

const cityPath = (slug: LjudLjusCitySlug) => `/ljud-ljus-${slug}`;

export const LJUD_LJUS_CITY_CONFIG: Record<LjudLjusCitySlug, LjudLjusCityConfig> = {
  malmo: {
    slug: 'malmo',
    path: cityPath('malmo'),
    displayName: 'Malmö',
    breadcrumbLabel: 'Ljud & ljus Malmö',
    serviceSchemaName: 'Ljud och ljus i Malmö',
    serviceSchemaDescription:
      'Uthyrning och planering av ljud och ljus i Malmö: PA, uplights, effekter och paket till fest, företag och bröllop. Leverans i Malmö med omnejd.',
    heroEyebrow: 'Lokalt · Malmö & närområde',
    heroTitle: 'Hyra ljud och ljus i Malmö',
    heroLead:
      'En samlad kontaktväg för tal, dansgolv och stämning – oavsett om ni är i en lokal vid Triangeln, i tält vid Ribersborg, på kontoret i Hyllie eller i en villa i Limhamn. Vi dimensionerar efter sal, vind utomhus och hur mycket ni vill att gästerna ska känna basen.',
    seoSectionId: 'seo-ljud-ljus-malmo',
    seoSectionTitle: 'Ljud och ljus som matchar Malmös lokaler',
    introParagraphs: [
      'Malmö är tätt, varierat och ofta logistiskt utmanande: korta lastzoner, hiss som inte rymmer subbar och grannar som ska sova när festen är i källarlokal. Därför börjar vi med plats och tidslinje – inte bara watt på papper – så att PA och ljus faktiskt funkar när toastmaster, DJ och mingel ska samsas samma kväll.',
      'Många bokar oss när de vill slippa att pussla ihop flera leverantörer. Samma team tänker på var högtalarna står, var uplights inte bländar vid bord och när effektljus får ta plats utan att störa fotograf eller lokalens regler. Vi utgår från Malmö med leverans i hela Skåne – korta riggar och snabba återbesök är vardag i regionen.',
    ],
    quotePrimary: 'Ljud & ljus Malmö',
    quoteSeo: 'Ljud & ljus Malmö',
    ctaLabel: 'Få offert på ljud & ljus',
    geo: { latitude: 55.60587, longitude: 13.00073 },
    showMalmoDeepLinks: true,
    // Sound Expansion FINAL GO 2026-10-08 — body only; title/H1/meta/canonical NO_CHANGE
    soundPaRentalSection: {
      id: 'seo-ljud-pa-uthyrning',
      title: 'Hyra PA och ljudanläggning till fest och event',
      paragraphs: [
        'När ni söker hyra ljud, hyra PA-system eller en komplett ljudanläggning till fest behöver ni mer än en enskild högtalare i katalogen. Här utgår vi från sal, antal gäster och program — tal, mingel och dans — och sätter ihop en PA-nivå som orkar kvällen utan onödig watt.',
        'Ljudsystem till event och ljudanläggning till fest är samma typ av uppdrag: tydlig talbarhet tidigt, tryck när dansgolvet tar över, och leverans/rigg som passar Malmös lokaler. Behöver ni däremot främst bashögtalare till en enklare musikfest i Malmö pekar vi vidare till vår dedikerade sida för hyra högtalare i Malmö — den här sidan äger den bredare ljuduthyrningen.',
      ],
      hogtalareLinkAnchor: 'hyra högtalare i Malmö',
      hogtalareLinkPath: '/hyra-hogtalare-malmo',
    },
    faqItems: [
      {
        q: 'Hyra ljud och ljus i Malmö – vad skiljer er från en ren PA-uthyrning?',
        a: 'Här får ni helhetsplanering: ljud, ljus och effekter i samma offert och samma genomgång. Behöver ni mest högtalare finns också en dedikerad sida för hyra högtalare Malmö – men den här sidan är för er som vill att stämningen ska hänga ihop från tal till dans.',
      },
      {
        q: 'Kan ni leverera samma dag i Malmö innerstad?',
        a: 'Ofta ja under vardagar beroende på utrustning och redan bokade riggar. Skriv adress, tidsfönster för lastning och om hiss/trappa finns – då ser vi om schemat håller.',
      },
      {
        q: 'Vi har bröllop – passar den här sidan oss?',
        a: 'Ja för den generella vinkeln fest och lokal. Vill ni djupdyka i vigsel, toastmaster och bröllopsljus finns även vår sida om ljud och ljus till bröllop.',
      },
      {
        q: 'Ingår tekniker på plats?',
        a: 'Det beror på paket och komplexitet. I offerten anger vi tydligt om körning är självhämtning, leverans med genomgång eller bemannad kväll – så slipper ni gissa.',
      },
      {
        q: 'Kan vi kombinera med scen?',
        a: 'Ja. När programmet kräver talarhöjd eller större yta kan scen passa ihop med ljus och PA. Se hyra scen Malmö för scenfokus – här utgår vi från ljud och ljus som bärande spår.',
      },
      {
        q: 'Jobbar ni utanför Malmö?',
        a: 'Ja, i hela Skåne. För översikt och fler orter, se ljud och ljus Skåne-hubben.',
      },
      // Sound Expansion FINAL GO — append only
      {
        q: 'Hyra PA-system eller ljudanläggning till fest – hur dimensionerar ni?',
        a: 'Vi utgår från lokal, antal gäster och om kvällen går från tal till dans. Ni får en tydlig utrustningslista i offerten — inte bara en katalograd — så att PA:n orkar utan att ni överbokar.',
      },
      {
        q: 'Vad är skillnaden mellan den här sidan och hyra högtalare Malmö?',
        a: 'Hyra högtalare Malmö är för er som främst behöver högtalare/PA i Malmö. Den här sidan är för bredare ljuduthyrning till fest och event — inklusive när ljud ska hänga ihop med ljus och leveransplan. Länken till hyra högtalare Malmö finns kvar när det är rätt ingång.',
      },
      {
        q: 'Ljudsystem till event – ingår tekniker?',
        a: 'Det beror på komplexitet. I offerten anger vi om det är leverans med genomgång, självhämtning eller bemannad kväll — så ni vet vad som ingår innan ni bokar.',
      },
    ],
  },
  lund: {
    slug: 'lund',
    path: cityPath('lund'),
    displayName: 'Lund',
    breadcrumbLabel: 'Ljud & ljus Lund',
    serviceSchemaName: 'Ljud och ljus i Lund',
    serviceSchemaDescription:
      'Uthyrning av ljud och ljus i Lund: studentfester, nationer, företagsevent och akademiska lokaler. Leverans från Skåne med tydlig offert.',
    heroEyebrow: 'Lokalt · Lund & universitetsstad',
    heroTitle: 'Hyra ljud och ljus i Lund',
    heroLead:
      'Lund blandar stora sittningar, akademiska salar och små innergårdar – ofta med stränga tider för uppbyggnad och krav på diskret teknik i kulturhusmiljöer. Vi tänker på både volym mot grannar och tydlighet när tal och musik växlar under samma kväll.',
    seoSectionId: 'seo-ljud-ljus-lund',
    seoSectionTitle: 'Ljud och ljus anpassat för Lund',
    introParagraphs: [
      'Många förfrågningar handlar om nationer, alumni-dagar eller företag nära Ideon och MAX IV-området – där behövs ibland snabb rigg mellan dagkonferens och kvällsfest. Vi packar för trappor och trånga entréer där det är vanligt i äldre fastigheter kring stadskärnan.',
      'Avståndet till vår bas i Malmö är kort, vilket gör Lund till en naturlig systerort i samma leveransområde. Ni får samma tydliga postlista i offerten som på våra övriga sidor – bara med Lund i fokus i planeringen.',
    ],
    quotePrimary: 'Ljud & ljus Lund',
    quoteSeo: 'Ljud & ljus Lund',
    ctaLabel: 'Få offert på ljud & ljus',
    geo: { latitude: 55.70466, longitude: 13.191 },
    showMalmoDeepLinks: false,
    faqItems: [
      {
        q: 'Hyra ljud i Lund – gäller samma leveransområde som Malmö?',
        a: 'Ja, vi planerar utifrån Skåne med kort transport till Lund. Exakt pris beror på datum, tidsfönster och utrustningsvolym – skriv lokal och tid så återkommer vi.',
      },
      {
        q: 'Kan ni jobba i akademiska lokaler med sträng ljudpolicy?',
        a: 'Vi tar hänsyn till lokalens regler och grannar. Beskriv sal eller gård, tidsprogram och om volym ska begränsas – då föreslår vi lämplig PA-nivå och ljus utan onödig störning.',
      },
      {
        q: 'Passar ni studentfester och sittningar?',
        a: 'Ja, ofta i kombination med tydligt tal under middag och mer tryck senare. Se även vår sida om att hyra ljud till studentfest för paketperspektiv.',
      },
      {
        q: 'Finns dedikerade sidor för högtalare eller scen i Lund?',
        a: 'I första skede pekar vi mot produktkatalogen och Skåne-hubben. För scen i Malmöregionen finns hyra scen Malmö som referens för liknande upplägg.',
      },
      {
        q: 'Kan vi få genomgång innan kvällen?',
        a: 'Rekommenderas vid flera talare eller växling mellan band och DJ. Säg till i förfrågan så lägger vi in tid för genrep enligt ert schema.',
      },
    ],
  },
  helsingborg: {
    slug: 'helsingborg',
    path: cityPath('helsingborg'),
    displayName: 'Helsingborg',
    breadcrumbLabel: 'Ljud & ljus Helsingborg',
    serviceSchemaName: 'Ljud och ljus i Helsingborg',
    serviceSchemaDescription:
      'Uthyrning av ljud och ljus i Helsingborg: företagsevent, stadens lokaler, bröllop och konsert. Leverans i nordvästra Skåne.',
    heroEyebrow: 'Lokalt · Helsingborg & nordvästra Skåne',
    heroTitle: 'Hyra ljud och ljus i Helsingborg',
    heroLead:
      'Från hamnkvarter och hotell vid Sundet till industrilokaler och idrottshallar i närförorten: Helsingborg har höga förväntningar på tydligt tal och tryck i dansgolvet. Vi riggar för både vind från vattnet och salar där akustiken kräver omtanke – inte bara “mer volym”.',
    seoSectionId: 'seo-ljud-ljus-helsingborg',
    seoSectionTitle: 'Eventljud och ljus i Helsingborg',
    introParagraphs: [
      'Stadskärnan och hamnområdet bjuder på mixade format: mässor, produktlanseringar, jubileumsfester och sommarevent där utomhusdelen behöver egen plan för väder och publikflöden. Vi följer er tidslinje från rigg till nedtag så att tekniken inte stjäl fokus från programmet.',
      'Med färja- och tågnärvaro hit är logistiken ibland annorlunda än i Malmö – därför vill vi gärna veta lastväg och parkering tidigt. Samma organisation som driver festutrustning.se i Skåne står för offerten, så ni vet vad som ingår innan ni låser datum.',
    ],
    quotePrimary: 'Ljud & ljus Helsingborg',
    quoteSeo: 'Ljud & ljus Helsingborg',
    ctaLabel: 'Få offert på ljud & ljus',
    geo: { latitude: 56.04673, longitude: 12.69437 },
    showMalmoDeepLinks: false,
    faqItems: [
      {
        q: 'Levererar ni till Helsingborg och omnejd?',
        a: 'Ja, hela nordvästra Skåne ingår i vårt normala område. Skriv plats och datum så räknar vi transport och riggtid.',
      },
      {
        q: 'Kan vi hyra både ljus och PA i samma bokning?',
        a: 'Det är syftet med den här sidan – en kontakt för ljusbild och ljudbild som hänger ihop. I offerten listar vi utrustning och ev. tekniker.',
      },
      {
        q: 'Vad gäller för utomhus vid vattnet?',
        a: 'Vind, fukt och el måste planeras. Beskriv yta, förväntat antal gäster och om ni har tent – då föreslår vi skydd för utrustning och säkra kablage.',
      },
      {
        q: 'Har ni referenser från företagsevent?',
        a: 'Läs gärna omdömen under våra företagsrelaterade sidor och produktsida. I förfrågan kan ni ange bransch så matchar vi er med liknande riggar vi gjort.',
      },
      {
        q: 'Var hittar jag helhetsöversikt för Skåne?',
        a: 'Se vår sida om ljud och ljus Skåne för hubb med länkar till fler orter och teman.',
      },
    ],
  },
};

export function getLjudLjusCityConfig(slug: LjudLjusCitySlug): LjudLjusCityConfig {
  return LJUD_LJUS_CITY_CONFIG[slug];
}
